import { useMemo } from 'react';

import moment from 'moment';

import { CalculatorFrequency, GetChartRequestBody, GetChartResponseBody } from '@sparkplug/lib';

import { frequencyMap, getCloudChartData } from '@helpers/charts';

import { IChartDataSettings } from '@app/types/ChartDataTypes';

import { useAdvancedQuery } from '../QueryHooks';

export const useCloudChartDataQuery = (
    retailerAccountId: string,
    settings: Pick<
        IChartDataSettings,
        | 'metric'
        | 'precision'
        | 'locationIds'
        | 'brandGroupId'
        | 'dateStart'
        | 'dateEnd'
        | 'filters'
        | 'sparkId'
        | 'percentIncreaseData'
    >,
    isEnabled: boolean,
) => {
    const params = useMemo<GetChartRequestBody>(
        () => ({
            locationIds: settings.locationIds,
            frequency: frequencyMap[settings.precision] as CalculatorFrequency,
            groupId: retailerAccountId,
            brandGroupId: settings.brandGroupId,
            dateStart: moment(settings.dateStart).format('YYYY-MM-DD'),
            dateEnd: moment(settings.dateEnd).format('YYYY-MM-DD'),
            sparkId: settings.sparkId,
            productIds: settings.filters?.products?.map(({ _id }) => _id),
            percentIncreaseData: settings.percentIncreaseData,
        }),
        [settings, retailerAccountId],
    );

    const fetchChartData = (): Promise<GetChartResponseBody> => {
        /**
         * In order to show "No Sales Data" message, we need to return an empty chart data if there are no locationIds
         * so that we don't hit the endpoint (which will return an error)
         */
        if (!settings.locationIds.length) {
            return Promise.resolve({
                total: 0,
                locationTotals: {},
                locationBuckets: {},
                employeeTotals: {},
                employeeBuckets: {},
                employeeTransactionCounts: {},
                productsWithSalesByKey: [],
                productBuckets: {},
                commissions: {},
            });
        }

        return getCloudChartData({ type: settings.metric }, params);
    };

    return useAdvancedQuery(['chart', settings.metric, JSON.stringify(params)], fetchChartData, {
        enabled: isEnabled && !!settings.metric,
    });
};
